import type { BuyerVerificationStatus } from './dto/buyer-verification.js';

/**
 * SIL-2171: the KYB state an org sees in the Escrow.com payments panel.
 *
 * "Cleared by SiliconX" = the org's buyer verification is verified (an ops_admin approved it, SIL-1313). It is not the
 * per-offer `kyb_cleared` checklist tick; that waits on ops separately and is reported as a deal-level line
 * (`buyer_awaiting_ops`, migration 1172).
 *
 * Sellers: SiliconX collects no seller KYB yet (deferred to SIL-2156 pending counsel), so the seller state is a constant.
 * The `input` argument is the seam item 3 will fill.
 */
export type OrgKybStateKey = 'not_started' | 'submitted' | 'cleared';

export interface OrgKybState {
  state: OrgKybStateKey;
  label: string;
}

const BUYER_LABELS: Record<OrgKybStateKey, string> = {
  not_started: 'Not started',
  submitted: 'Submitted, SiliconX is reviewing',
  cleared: 'Cleared by SiliconX',
};

export const SELLER_KYB_NOT_COLLECTED_LABEL = 'Not started. SiliconX does not collect seller KYB yet.';

export const KYB_AWAITING_OPS_LINE =
  'SiliconX is clearing KYB for an accepted offer. Your Escrow.com deposit link follows.';

export function orgKybState(
  side: 'buyer' | 'seller',
  input?: { buyerStatus?: BuyerVerificationStatus | null },
): OrgKybState {
  if (side === 'seller') return { state: 'not_started', label: SELLER_KYB_NOT_COLLECTED_LABEL };
  const s = input?.buyerStatus;
  const state: OrgKybStateKey = s === 'verified' ? 'cleared' : s === 'pending' ? 'submitted' : 'not_started';
  return { state, label: BUYER_LABELS[state] };
}
